import { makeStyles } from '@material-ui/core';
import Typography, {
  TypographyProps as MUITypographyProps,
} from '@material-ui/core/Typography';

import React from 'react';
import cx from 'classnames';

const useStyles = makeStyles(t => ({
  'textTransform-uppercase': {
    textTransform: 'uppercase',
  },
  'textTransform-none': {
    textTransform: 'none',
  },
  'overflow-visible': {
    overflow: 'hidden',
    whiteSpace: 'initial',
  },
  'overflow-initial': {
    overflow: 'hidden',
    textOverflow: 'initial',
  },
  'overflow-ellipsis': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  'overflow-clip': {
    overflow: 'hidden',
    textOverflow: 'clip',
  },
  'color-default': {
    color: t.palette.grey[700],
  },
  'color-muted': {
    color: t.palette.grey[500],
  },
  'color-mint': {
    color: t.palette.mint.main,
  },
  'color-error': {
    color: t.palette.error.main,
  },
  'color-white': {
    color: t.palette.white.main,
  },
  h1: {
    fontWeight: 'bold',
    fontSize: '1.5rem',
    lineHeight: 1.20588,
    [t.breakpoints.up('md')]: {
      fontSize: '1.875rem',
    },
  },
  h1Light: {
    color: t.palette.grey[500],
    fontSize: '1.5rem',
    lineHeight: 1.20588,
    [t.breakpoints.up('md')]: {
      fontSize: '1.875rem',
    },
  },
  ch1: {
    fontWeight: 'bold',
    fontSize: '1.5rem',
    [t.breakpoints.up('md')]: {
      fontSize: '1.875rem',
    },
    color: t.palette.white.main,
    backgroundColor: t.palette.grey[700],
    padding: '1rem',
  },
  h2: {
    fontWeight: 'bold',
    fontSize: '1.125rem',
    lineHeight: 1.35417,
    [t.breakpoints.up('md')]: {
      fontSize: '1.5rem',
    },
  },
  h3: {
    fontWeight: 'bold',
    fontSize: '1rem',
    lineHeight: 1.16667,
    [t.breakpoints.up('md')]: {
      fontSize: '1.25rem',
    },
  },
  h4: {
    fontWeight: 'bold',
    fontSize: '0.875rem',
    lineHeight: 1.5,
    [t.breakpoints.up('md')]: {
      fontSize: '1.125rem',
    },
  },
  paragraphTitle: {
    fontWeight: 'bold',
    fontSize: '1rem',
    lineHeight: '1.625rem',
  },
  paragraph: {
    fontWeight: 'normal',
    fontSize: '1rem',
    lineHeight: 1.5,
  },
  caption: {
    fontWeight: 'normal',
    fontSize: '0.875rem',
    lineHeight: 1.375,
  },
  helper: {
    fontWeight: 'normal',
    fontSize: '0.75rem',
    lineHeight: 1.333,
  },
  button: {
    fontWeight: 'normal',
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    lineHeight: 1.5,
  },
  // last to overwrite if needed
  'fontWeight-normal': {
    fontWeight: 'normal',
  },
  'fontWeight-bold': {
    fontWeight: 'bold',
  },
  'lineHeight-1': {
    lineHeight: 1,
  },
}));

export type OwnVariant =
  | 'h1'
  | 'h1Light'
  | 'ch1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'paragraphTitle'
  | 'paragraph'
  | 'caption'
  | 'helper'
  | 'button';

export type Color =
  | 'muted'
  | 'mint'
  | 'error'
  | 'white'
  | 'inherit'
  | 'default';
export type Overflow = 'ellipsis' | 'clip' | 'initial' | 'visible';
export type TextTransform = 'uppercase' | 'none';
export type FontWeight = 'bold' | 'normal';
export type Align = MUITypographyProps['align'];
export type Variant = MUITypographyProps['variant'];

type ComponentType =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'span'
  | 'div'
  | 'p';

interface Props {
  color?: Color;
  textTransform?: TextTransform;
  fontWeight?: FontWeight;
  noWrap?: boolean;
  overflow?: Overflow;
  component?: ComponentType;
  align?: Align;
  className?: string;
  ownVariant: OwnVariant;
  variant: Variant;
  display?: MUITypographyProps['display'];
  lineHeight?: 1;
}

type ExternalProps = Omit<Props, 'variant' | 'ownVariant'>;

const MuiTypography: React.FC<MUITypographyProps<
  'span',
  { component?: ComponentType }
>> = props => <Typography {...props} />;

// Typography: https://housinganywhere.atlassian.net/wiki/spaces/DS/pages/914489352/Typography
// Description: This component is in charge of setting up predifined styles from DSL and
// allowing you to choose between default and muted color text.
const CustomTypography: React.FC<Props> = ({
  className,
  textTransform,
  fontWeight,
  children,
  noWrap,
  component,
  variant,
  ownVariant,
  color = 'default',
  overflow = 'initial',
  display = 'block',
  lineHeight,
  align,
  ...rest
}) => {
  const classes = useStyles();

  return (
    <MuiTypography
      className={cx(
        className,
        classes[ownVariant],
        classes[`overflow-${overflow}`],
        classes[`color-${color}`],
        classes[`textTransform-${textTransform}`],
        classes[`fontWeight-${fontWeight}`],
        classes[`lineHeight-${lineHeight}`],
      )}
      component={component}
      noWrap={
        overflow !== 'visible' &&
        (noWrap || overflow === 'ellipsis' || overflow === 'clip')
      }
      variant={variant}
      align={align}
      display={display}
      {...rest}
    >
      {children}
    </MuiTypography>
  );
};

function make(variant: Variant, ownVariant: OwnVariant) {
  const TypographyNext: React.FC<ExternalProps> = props => (
    <CustomTypography {...props} variant={variant} ownVariant={ownVariant} />
  );

  TypographyNext.displayName = `Typography.${ownVariant}`;

  return TypographyNext;
}

export enum TypographyTypes {
  Button = 'Button',
  Caption = 'Caption',
  CH1 = 'CH1',
  H1 = 'H1',
  H1Light = 'H1Light',
  H2 = 'H2',
  H3 = 'H3',
  H4 = 'H4',
  H5 = 'H5',
  H6 = 'H6',
  HelperText = 'HelperText',
  Paragraph = 'Paragraph',
  ParagraphTitle = 'ParagraphTitle',
}

// We expose different typographies through Typography component
// so you can use Typography.Variant
// Different styles can be applied in the future altough right now
// it is about color, but we could manage font size by breakpoints.
export default {
  [TypographyTypes.Button]: make('button', 'button'),
  [TypographyTypes.Caption]: make('caption', 'caption'),
  [TypographyTypes.CH1]: make('h1', 'ch1'),
  [TypographyTypes.H1]: make('h1', 'h1'),
  [TypographyTypes.H1Light]: make('h1', 'h1Light'),
  [TypographyTypes.H2]: make('h2', 'h2'),
  [TypographyTypes.H3]: make('h3', 'h3'),
  [TypographyTypes.H4]: make('h4', 'h4'),
  [TypographyTypes.H5]: make('h5', 'h5'),
  [TypographyTypes.H6]: make('h6', 'h6'),
  [TypographyTypes.HelperText]: make('caption', 'helper'),
  [TypographyTypes.Paragraph]: make('body1', 'paragraph'),
  [TypographyTypes.ParagraphTitle]: make('body2', 'paragraphTitle'),
};
